/**
 * The call-off's lines, grouped warehouse / direct, with the pallet and weight
 * summary under them — what the detail modal's Items tab shows.
 *
 * CR-7782: split out of CallOffLinesTable so the Call Off Management list can
 * show the same thing in a row's expanded view, without opening the modal.
 * Read-only unless the editing props are passed; the Items tab passes them for
 * the supplier's batch edit.
 *
 * @link CallOffLinesTable for the Items tab, which adds print and add-parts.
 * @link CallOffExpandedItems for the list's expanded row.
 */
import type { CallOff, CallOffLine } from '../../../lib/api';
import { plainDecimal } from '../../../lib/plainDecimal';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { PRIORITY_EDGE_CLASS, priorityTitle } from '../shared/linePriority';
import PriorityBadge from '../shared/PriorityBadge';
import ColourKey, { type ColourKeyEntry } from '../shared/ColourKey';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { Trash2, Warehouse, Truck } from 'lucide-react';

export interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

interface CallOffLinesGridProps {
  callOff: CallOff;
  /** The supplier's batch edit is open: quantities become inputs. */
  inlineEditMode?: boolean;
  inlineEditChanges?: Record<number, InlineEditChange>;
  onQuantityChange?: (line: CallOffLine, newQty: string) => void;
  removeLineItem?: (line: CallOffLine) => void;
  /** Per-part PO availability budget. When a warehouse line's part is over,
   *  the qty input goes red and the parent disables Save Changes. */
  overByPart?: Record<string, { effective: number; consumed: number; over: boolean }>;
}

const NO_CHANGES: Record<number, InlineEditChange> = {};

/** CR-8613: the colours this grid can show while quantities are being edited. */
const EDIT_COLOUR_KEY: readonly ColourKeyEntry[] = [
  { swatchClassName: 'bg-red-50', label: 'Pink: more than is available for that part' },
  { swatchClassName: 'bg-amber-50', label: 'Amber: an unsaved change' },
];

/** CR-8613: the colours it shows at any time. */
const COLOUR_KEY: readonly ColourKeyEntry[] = [
  { swatchClassName: 'shadow-[inset_3px_0_0_0_#f97316]', label: 'Orange edge: priority line' },
  { swatchClassName: 'bg-green-100', label: 'Green Fill %: a full load or more' },
  { swatchClassName: 'bg-amber-100', label: 'Amber Fill %: half a load or more' },
];

export default function CallOffLinesGrid({
  callOff, inlineEditMode = false, inlineEditChanges = NO_CHANGES,
  onQuantityChange, removeLineItem, overByPart,
}: CallOffLinesGridProps) {
  // CR-7732: this tab is about what is on the load — parts, quantities,
  // pallets and weights. Price per unit and line total are gone from it for
  // everyone: the commercial terms belong on the order, not on the packing
  // detail people read to load a lorry. (The backend still withholds price
  // from a haulier outright; that has not changed.)
  const { formatDateTime } = useFormatters();
  const lines = callOff.lines ?? [];
  const warehouseLines = lines.filter((l: CallOffLine) => l.delivery_type === 'warehouse');
  const directLines = lines.filter((l: CallOffLine) => l.delivery_type !== 'warehouse');
  // CR-6173: the Pallets column is now shown to every role (customer / supplier
  // / logistics), so it is always part of the base column count.
  // CR-6291: +2 for the Unit Wt and Line Wt columns.
  // CR-7732: the Sales Order column is only there for direct deliveries — a
  // warehouse line has none, so on a warehouse-only load it was a column of
  // dashes.
  const hasDirectDelivery = directLines.length > 0;
  const colSpan = 10 + (hasDirectDelivery ? 1 : 0) + (inlineEditMode ? 1 : 0);

  // The qty in play for a line — the in-progress batch-edit value when one is
  // being edited, otherwise the requested qty — so pallet/weight totals stay live.
  const qtyForLine = (line: CallOffLine): number => {
    const change = inlineEditChanges[line.id];
    return parseFloat(change ? change.newQty : line.requested_qty) || 0;
  };

  // Pallets per line for the logistics view. CR-6173: the haulier books whole
  // pallets, so round up to the next whole pallet — a part-filled pallet still
  // occupies a full pallet space.
  const palletsForLine = (line: CallOffLine): number => {
    const palletQty = parseFloat(line.pallet_qty) || 0;
    return palletQty > 0 ? Math.ceil(qtyForLine(line) / palletQty) : 0;
  };

  // CR-6291: per-line gross weight = qty × unit weight (kg) from the Parts Master.
  const weightForLine = (line: CallOffLine): number => {
    const unit = parseFloat(line.unit_weight_kg ?? '') || 0;
    return qtyForLine(line) * unit;
  };

  const warehousePalletTotals = warehouseLines.reduce<Record<string, number>>((acc, line) => {
    const dest = line.delivery_destination_name || 'Warehouse';
    acc[dest] = (acc[dest] || 0) + palletsForLine(line);
    return acc;
  }, {});
  const directPalletTotal = directLines.reduce((sum, line) => sum + palletsForLine(line), 0);
  const grandPalletTotal =
    Object.values(warehousePalletTotals).reduce((a, b) => a + b, 0) + directPalletTotal;
  // CR-6291: total gross weight for the whole load/lorry.
  const grandWeightTotal = lines.reduce((sum, line) => sum + weightForLine(line), 0);

  const renderLine = (line: CallOffLine) => {
    const batchChange = inlineEditChanges[line.id];
    const displayQty = batchChange ? batchChange.newQty : line.requested_qty;
    const loadQty = parseFloat(line.load_qty) || 0;
    const lineQty = parseFloat(displayQty) || 0;
    const lineFill = loadQty > 0 ? Math.round((lineQty / loadQty) * 100) : 0;
    const palletQty = parseFloat(line.pallet_qty) || 0;
    const pallets = palletQty > 0 ? palletsForLine(line) : '—';
    const unitWeight = parseFloat(line.unit_weight_kg ?? '') || 0;
    // Exactly as the data carries it — a 0.0045 kg part x 3 reads 0.0135, not 0.01.
    const lineWeightLabel = unitWeight > 0 ? plainDecimal(weightForLine(line)) : '—';
    const partInfo = line.delivery_type === 'warehouse' ? overByPart?.[line.part_number] : undefined;
    const isOver = !!partInfo?.over;
    // CR-7744: the row fill belongs to an over-limit error (red) or an unsaved
    // edit (amber). A priority line is marked by its orange edge bar and
    // badge instead, so it never reads as an unsaved edit and never hides one.
    const rowClass = isOver ? 'bg-red-50' : batchChange ? 'bg-amber-50' : '';
    // CR-8613: a coloured row says why it is that colour.
    const rowTitle = isOver
      ? 'Pink: more than is available for this part'
      : batchChange ? 'Amber: an unsaved change' : undefined;

    return (
      <TableRow key={line.id} className={rowClass} title={rowTitle}>
        <TableCell className={`font-mono text-xs font-medium ${line.is_priority ? PRIORITY_EDGE_CLASS : ''}`}>
          {line.part_number}
          {line.is_priority && (
            <PriorityBadge
              className="ml-1.5"
              title={priorityTitle(line.priority_set_by_name, line.priority_set_at, formatDateTime)}
            />
          )}
        </TableCell>
        <TableCell className="text-sm">{line.description}</TableCell>
        <TableCell>
          {line.delivery_type === 'warehouse'
            ? line.delivery_destination_name || 'Warehouse'
            : line.delivery_address || line.sales_order || 'Direct'}
        </TableCell>
        {hasDirectDelivery && (
          <TableCell className="font-mono text-xs">
            {line.sales_order || '—'}
          </TableCell>
        )}
        <TableCell className="text-right">
          {inlineEditMode && onQuantityChange ? (
            <div className="flex flex-col items-end gap-0.5">
              <Input
                type="number"
                className={`h-7 w-20 text-right text-sm ${
                  isOver ? 'border-red-500 bg-red-50 text-red-700 focus-visible:ring-red-500' : ''
                }`}
                value={displayQty}
                onChange={(e) => onQuantityChange(line, e.target.value)}
                min={0}
              />
              {isOver && partInfo && (
                <span className="text-[10px] text-red-700 whitespace-nowrap">
                  {partInfo.effective === 0
                    ? 'No PO available'
                    : `Only ${partInfo.effective} available`}
                </span>
              )}
            </div>
          ) : (
            <span>{line.requested_qty}</span>
          )}
        </TableCell>
        <TableCell className="text-right">{line.load_qty}</TableCell>
        <TableCell className="text-right font-medium">{pallets}</TableCell>
        {/* CR-6291: gross weight per product + qty × unit line total (kg). */}
        <TableCell className="text-right">{unitWeight > 0 ? plainDecimal(line.unit_weight_kg) : '—'}</TableCell>
        <TableCell className="text-right font-medium">{lineWeightLabel}</TableCell>
        <TableCell className="text-right">
          <Badge
            variant="secondary"
            className={lineFill >= 100 ? 'bg-green-100 text-green-700' : lineFill >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}
          >
            {lineFill}%
          </Badge>
        </TableCell>
        <TableCell>
          <span className="text-xs text-muted-foreground">{line.notes || ''}</span>
        </TableCell>
        {inlineEditMode && (
          <TableCell className="text-center">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-red-500 hover:text-red-700"
              onClick={() => removeLineItem?.(line)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TableCell>
        )}
      </TableRow>
    );
  };

  if (lines.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No lines.</div>;
  }

  return (
    <>
      <ColourKey
        entries={inlineEditMode ? [...EDIT_COLOUR_KEY, ...COLOUR_KEY] : COLOUR_KEY}
        className="mb-2"
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Part Number</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Destination</TableHead>
            {/* CR-7142: the supplier's sales order, so the warehouse knows
                which PO line to take the stock off. It was reachable only on
                the ASN detail before. */}
            {hasDirectDelivery && <TableHead>Sales Order</TableHead>}
            <TableHead className="text-right">Requested</TableHead>
            <TableHead className="text-right">Load Qty</TableHead>
            <TableHead className="text-right">Pallets</TableHead>
            <TableHead className="text-right">Unit Wt (kg)</TableHead>
            <TableHead className="text-right">Line Wt (kg)</TableHead>
            <TableHead className="text-right">Fill %</TableHead>
            <TableHead>Notes</TableHead>
            {inlineEditMode && <TableHead className="text-center">Action</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {warehouseLines.length > 0 && (
            <>
              <TableRow className="bg-red-50 hover:bg-red-50">
                <TableCell colSpan={colSpan} className="py-2">
                  <div className="flex items-center gap-1.5 font-semibold text-red-700 text-xs uppercase tracking-wide">
                    <Warehouse className="h-3.5 w-3.5" />
                    Warehouse Delivery ({warehouseLines.length} items)
                  </div>
                </TableCell>
              </TableRow>
              {warehouseLines.map(renderLine)}
            </>
          )}
          {directLines.length > 0 && (
            <>
              <TableRow className="bg-blue-50 hover:bg-blue-50">
                <TableCell colSpan={colSpan} className="py-2">
                  <div className="flex items-center gap-1.5 font-semibold text-blue-700 text-xs uppercase tracking-wide">
                    <Truck className="h-3.5 w-3.5" />
                    Direct Delivery ({directLines.length} items)
                  </div>
                </TableCell>
              </TableRow>
              {directLines.map(renderLine)}
            </>
          )}
        </TableBody>
      </Table>

      {/* CR-6173/CR-6291: pallet + gross weight totals — shown to all roles. */}
      <Card className="mt-4 border-blue-200">
        <CardHeader className="py-2 px-4 bg-blue-50">
          <CardTitle className="text-sm flex items-center gap-2">
            <Truck className="h-4 w-4" />
            Pallet &amp; Weight Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-1.5 text-sm">
            {Object.entries(warehousePalletTotals).map(([dest, total]) => (
              <div key={dest} className="flex justify-between">
                <span className="text-muted-foreground">{dest}</span>
                <span className="font-medium">{total} pallets</span>
              </div>
            ))}
            {directPalletTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Direct Deliveries</span>
                <span className="font-medium">{directPalletTotal} pallets</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-1.5 mt-1.5 font-semibold">
              <span>Total Pallets</span>
              <span>{grandPalletTotal} pallets</span>
            </div>
            {/* CR-6291: total gross weight for the whole load/lorry. */}
            <div className="flex justify-between font-semibold">
              <span>Total Gross Weight</span>
              <span>{plainDecimal(grandWeightTotal)} kg</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
